import { useState } from "react";
import { Clock, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

const HEADLINE = "When disruption hits a freight corridor, the challenge is not only visibility. It is deciding who may act, on what basis, and how the decision is evidenced across operational boundaries. FreightCierge turns that into a governed workflow.";

const SCRIPT_3 = [
  { t: "0:00", h: "Set the scene", b: "On the Explainer page: 'Disruption is rarely a visibility problem — it's a coordination problem. FreightCierge is a thin governance layer over the systems you already run.'" },
  { t: "0:30", h: "Open the live sandbox", b: "Point to the vessel header (4h ETA slip), yard at 92%, four containers including a pharma cut-off in ~90 minutes." },
  { t: "1:00", h: "Demonstrate three KATLAS outcomes", b: "Run the three actions in order: ALLOWED (terminal reslot), BLOCKED (haulage tries to release the doc-hold container), ESCALATED (port ops proposes priority handling for pharma)." },
  { t: "2:00", h: "Approve the escalation", b: "Switch to the Compliance Officer role and co-sign the escalated action. Show the receipt status flip from ESCALATED to APPROVED." },
  { t: "2:30", h: "Open the evidence pack", b: "Show one receipt — actor, authority, basis, linked event, downstream notifications. Export JSON. Close." },
];

const SCRIPT_7 = [
  { t: "0:00", h: "Headline + framing", b: HEADLINE },
  { t: "0:45", h: "Scenario context", b: "Storm + tidal window + 92% yard + pharma cut-off + compliance hold. Multi-party, time-sensitive." },
  { t: "1:30", h: "Roles in play", b: "Six roles, six different views of the same event — purpose-bound, not centralised." },
  { t: "2:30", h: "SiftyML predictive layer", b: "Toggle the panel: 'AI may recommend. It does not determine authority.' Highlight the cut-off miss probability." },
  { t: "3:15", h: "First KATLAS evaluation: ALLOWED", b: "Terminal Yard reslots a low-priority box. Within threshold. Receipt issued. Yard system notified." },
  { t: "3:45", h: "BLOCKED outcome", b: "Switch to Haulage. Attempt to gate out HLBU 220987-5. KATLAS denies — no decision rights. The attempt is itself recorded as a receipt." },
  { t: "4:30", h: "ESCALATED outcome", b: "Switch to Port Ops. Propose priority handling for the pharma container. KATLAS escalates. Co-sign required." },
  { t: "5:15", h: "Compliance approval", b: "Switch to Compliance. Approve the escalated receipt. Status moves to APPROVED with co-signed authority." },
  { t: "5:45", h: "RWI scenario stress (optional)", b: "Toggle on RWI. Push weather and customs sliders. Show 'unguided vs governed' comparison: ~60% faster resolution, 0 unauthorised actions, 96% evidence completeness." },
  { t: "6:30", h: "Evidence pack export", b: "Show the assembled receipts. Open one: actor, role, org, authority used, decision basis, linked event, downstream notifications. Export JSON." },
  { t: "7:00", h: "Close", b: "FreightCierge governs multi-party exception handling at the point of action. Existing systems stay in place. Attributable evidence comes out by default." },
];

function ScriptBlock({ rows, idLabel }) {
  return (
    <ol className="divide-y divide-[var(--line)] border border-[var(--line)]" data-testid={idLabel}>
      {rows.map((r, i) => (
        <li key={i} className="grid grid-cols-[80px_1fr] gap-6 px-5 py-4">
          <div className="mono text-[12px] text-[var(--ink-soft)] pt-1">{r.t}</div>
          <div>
            <div className="display text-[15px] mb-1">{r.h}</div>
            <p className="text-[14px] text-[var(--ink-soft)] leading-relaxed">{r.b}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function AssessorScript() {
  const [active, setActive] = useState("3");
  const [copied, setCopied] = useState(false);

  const text = (active === "3" ? SCRIPT_3 : SCRIPT_7)
    .map(r => `${r.t}  —  ${r.h}\n${r.b}`).join("\n\n");

  const onCopy = async () => {
    await navigator.clipboard.writeText(`${HEADLINE}\n\n${text}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-12 fc-fade-up">
      <div className="fc-eyebrow mb-3">Assessor demo scripts</div>
      <h1 className="display text-[40px] leading-tight mb-3">A 3-minute and a 7-minute version.</h1>
      <p className="text-[var(--ink-soft)] max-w-[60ch] mb-8">
        Use these scripts to drive the live sandbox during a Freight Innovation Fund assessor session.
        Both versions land the same headline in different lengths.
      </p>

      <div className="border-l-2 border-[var(--navy)] pl-5 py-2 bg-white mb-10" data-testid="script-headline">
        <div className="fc-eyebrow mb-2">Headline</div>
        <p className="text-[17px] leading-relaxed italic">"{HEADLINE}"</p>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <Button
          variant={active === "3" ? "default" : "outline"}
          onClick={() => setActive("3")}
          className={`rounded-sm h-10 ${active === "3" ? "bg-[var(--navy)] hover:bg-black" : ""}`}
          data-testid="tab-3min"
        >
          <Clock size={14} className="mr-2"/> 3-minute demo
        </Button>
        <Button
          variant={active === "7" ? "default" : "outline"}
          onClick={() => setActive("7")}
          className={`rounded-sm h-10 ${active === "7" ? "bg-[var(--navy)] hover:bg-black" : ""}`}
          data-testid="tab-7min"
        >
          <Clock size={14} className="mr-2"/> 7-minute demo
        </Button>
        <Button variant="ghost" onClick={onCopy} className="rounded-sm h-10 ml-auto" data-testid="copy-script-btn">
          {copied ? <><Check size={14} className="mr-2"/> Copied</> : <><Copy size={14} className="mr-2"/> Copy script</>}
        </Button>
      </div>

      {active === "3"
        ? <ScriptBlock rows={SCRIPT_3} idLabel="script-3-rows" />
        : <ScriptBlock rows={SCRIPT_7} idLabel="script-7-rows" />}
    </div>
  );
}
